import {useState, useEffect} from "react";
import {Box, Divider, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from "@mui/material";
import {Navigate, useParams, useSearchParams} from "react-router";
import AlignHorizontalLeftIcon from "@mui/icons-material/AlignHorizontalLeft";
import AlignHorizontalCenterIcon from "@mui/icons-material/AlignHorizontalCenter";
import AlignHorizontalRightIcon from "@mui/icons-material/AlignHorizontalRight";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles} from "../../../AppStyles.ts";
import type {TimeMark} from "../../../sdo/TimeMark.ts";
import {toggleGroupSx} from "../../network/intel/panels/resolvers/editors/ResolverUtils.tsx";
import TimelineCard from "./cards/TimelineCard.tsx";

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

type LayoutMode = "left" | "alternate" | "right";

// TODO: Replace with API call to TimelineManager.getMarks(year, month, day)
function buildTestData(year: number, month: number, day: number): TimeMark[] {
    const m = month - 1; // JS Date uses 0-based months
    return [
        {uid: "tm-1", time: new Date(year, m, day, 9, 15, 0).getTime(), precision: "DAY_HM", title: "Initial contact reported", details: "Field operative confirmed visual sighting at the designated location."},
        {uid: "tm-2", time: new Date(year, m, day, 14, 30, 0).getTime(), precision: "DAY_HM", title: "Document uploaded", details: "Source document received and catalogued into the resource library."},
        {uid: "tm-3", time: new Date(year, m, day, 18, 0, 0).getTime(), precision: "DAY_HR", title: "Analysis flag raised", details: "Automated enrichment process flagged this entity for analyst review."},
        {uid: "tm-4", time: new Date(year, m, day, 22, 45, 30).getTime(), precision: "DAY_HMS", title: "Network event logged", details: "Cross-reference match detected with an existing intelligence profile."},
    ];
}

// --- Spine constants ---

const SPINE_COL = 40;
const NODE_SIZE = 12;
const NODE_BORDER = 4;
const CONNECT_OVERLAP = (SPINE_COL / 2) - ((NODE_SIZE + NODE_BORDER * 2) / 2);

// --- Defaults for data-generated content ---

const NODE_COLOR_DATA = "#02cfee";
const CARD_ICON_DATA = "/images/timeline_white.svg";

// --- Layout toggle ---

const layoutToggleSx = {
    ...toggleGroupSx,
    "& .MuiToggleButton-root": {
        ...toggleGroupSx["& .MuiToggleButton-root"],
        padding: "4px 8px",
    },
};

function spineLeft(layout: LayoutMode): string {
    if (layout === "alternate") return `calc(50% - 1px)`;
    if (layout === "left") return `calc(100% - ${SPINE_COL / 2 + 1}px)`;
    return `${SPINE_COL / 2 - 1}px`;
}

// --- Timeline entry (spine node + connector + card) ---

function TimelineEntry({mark, side, layout, nodeColor = NODE_COLOR_DATA, icon = CARD_ICON_DATA}: {
    mark: TimeMark;
    side: "left" | "right";
    layout: LayoutMode;
    nodeColor?: string;
    icon?: string;
}) {
    const showLeft = layout === "alternate" || layout === "left";
    const showRight = layout === "alternate" || layout === "right";

    return (
        <Stack direction="row" alignItems="center" sx={{width: "100%", position: "relative"}}>
            {/* Left card column */}
            {showLeft && (
                <Box sx={{flex: 1, display: "flex", justifyContent: "flex-end", alignItems: "center", minHeight: 0}}>
                    {side === "left" ? (
                        <>
                            <TimelineCard mark={mark} icon={icon} />
                            <Box sx={{width: 20, minWidth: 20, height: 2, backgroundColor: "#3F464F", marginRight: `-${CONNECT_OVERLAP}px`}} />
                        </>
                    ) : null}
                </Box>
            )}

            {/* Spine node */}
            <Box sx={{
                width: SPINE_COL,
                minWidth: SPINE_COL,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}>
                <Box sx={{
                    width: NODE_SIZE,
                    height: NODE_SIZE,
                    borderRadius: "50%",
                    backgroundColor: nodeColor,
                    border: `${NODE_BORDER}px solid #3F464F`,
                    flexShrink: 0,
                    zIndex: 1,
                    boxSizing: "content-box",
                }} />
            </Box>

            {/* Right card column */}
            {showRight && (
                <Box sx={{flex: 1, display: "flex", justifyContent: "flex-start", alignItems: "center", minHeight: 0}}>
                    {side === "right" ? (
                        <>
                            <Box sx={{width: 20, minWidth: 20, height: 2, backgroundColor: "#3F464F", marginLeft: `-${CONNECT_OVERLAP}px`}} />
                            <TimelineCard mark={mark} icon={icon} />
                        </>
                    ) : null}
                </Box>
            )}
        </Stack>
    );
}

// --- Main viewer ---

function UXTimelineViewer() {
    const {networkUID, year: yearParam, month: monthParam, day: dayParam} = useParams();
    const [searchParams] = useSearchParams();
    const year = Number(yearParam);
    const month = Number(monthParam);
    const day = Number(dayParam);
    const hourFilter = searchParams.get("hour");
    const monthName = MONTH_SHORT[month - 1] ?? "";
    const dateLabel = `${monthName} ${day}, ${year}`;

    const [marks, setMarks] = useState<TimeMark[]>([]);
    const [loading, setLoading] = useState(true);
    const [layout, setLayout] = useState<LayoutMode>("alternate");

    useEffect(() => {
        // TODO: Replace with API call
        let data = buildTestData(year, month, day);
        data.sort((a, b) => b.time - a.time);

        if (hourFilter !== null) {
            const h = Number(hourFilter);
            data = data.filter(m => {
                const d = new Date(m.time);
                return d.getHours() === h;
            });
        }

        setMarks(data);
        setLoading(false);
    }, [networkUID, year, month, day, hourFilter]);

    function getSide(index: number): "left" | "right" {
        if (layout === "left") return "left";
        if (layout === "right") return "right";
        return index % 2 === 0 ? "left" : "right";
    }

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {!appSession.activeSession() ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%">
                    <WidgetHeader
                        title={`Timeline - ${dateLabel}`}
                        breadcrumbs={[
                            {display: "Networks", linkTo: "/networks/", activeView: false},
                            {display: String(networkUID), linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: "Timeline", linkTo: `/networks/${networkUID}/timeline`, activeView: false},
                            {display: String(year), linkTo: `/networks/${networkUID}/timeline/${year}`, activeView: false},
                            {display: monthName, linkTo: `/networks/${networkUID}/timeline/${year}/${month}`, activeView: false},
                            {display: String(day), linkTo: "", activeView: true},
                        ]}
                    />
                    <Stack sx={appStyles.sectionBox} spacing={0} width="100%" height="100%" alignItems="start">
                        {/* Toolbar */}
                        <Box sx={{width: "100%", padding: "12px 16px"}}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                                <Typography sx={{fontSize: "1rem", fontWeight: 600, color: "#F2F5FA", textAlign: "left"}}>
                                    Timeline for {dateLabel}
                                    {hourFilter !== null && (
                                        <Box component="span" sx={{color: "#a8b8c0", fontWeight: 400, ml: 1}}>
                                            ({formatHour(Number(hourFilter))})
                                        </Box>
                                    )}
                                </Typography>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={layout}
                                    onChange={(_e, v) => { if (v) setLayout(v as LayoutMode); }}
                                    sx={layoutToggleSx}
                                >
                                    <ToggleButton value="left">
                                        <Tooltip title="Cards on left"><AlignHorizontalLeftIcon sx={{fontSize: 18}} /></Tooltip>
                                    </ToggleButton>
                                    <ToggleButton value="alternate">
                                        <Tooltip title="Alternate"><AlignHorizontalCenterIcon sx={{fontSize: 18}} /></Tooltip>
                                    </ToggleButton>
                                    <ToggleButton value="right">
                                        <Tooltip title="Cards on right"><AlignHorizontalRightIcon sx={{fontSize: 18}} /></Tooltip>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                            <Divider sx={{borderColor: "#3F464F", mt: 1}} />
                        </Box>

                        {loading ? (
                            <Typography sx={{color: "#38ff82", fontSize: "0.85rem", padding: "16px"}}>Loading...</Typography>
                        ) : marks.length === 0 ? (
                            <Typography sx={{color: "#a8b8c0", fontSize: "0.85rem", padding: "16px"}}>No time marks found for this date.</Typography>
                        ) : (
                            <Box sx={{width: "100%", display: "flex", justifyContent: "center", padding: "8px 16px"}}>
                                <Box sx={{
                                    width: "100%",
                                    maxWidth: layout === "alternate" ? 960 : 520,
                                    position: "relative",
                                }}>
                                    {/* Continuous vertical spine line */}
                                    <Box sx={{
                                        position: "absolute",
                                        left: spineLeft(layout),
                                        top: 0,
                                        bottom: 0,
                                        width: 2,
                                        backgroundColor: "#3F464F",
                                        zIndex: 0,
                                    }} />

                                    {/* Timeline entries */}
                                    <Stack spacing={1.5}>
                                        {marks.map((mark, i) => (
                                            <TimelineEntry
                                                key={mark.uid}
                                                mark={mark}
                                                side={getSide(i)}
                                                layout={layout}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

function formatHour(hour: number): string {
    if (hour === 0) return "12 AM";
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return "12 PM";
    return `${hour - 12} PM`;
}

export default UXTimelineViewer;
